import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { APIProvider, useAPI } from './contexts/APIContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import RouteSeo from './components/RouteSeo';
import { TutorialProvider } from './contexts/TutorialContext';
import { PostHogProvider } from './contexts/PostHogContext';
import { SearchProvider } from './contexts/SearchContext';
import { EpisodeNotificationProvider } from './contexts/EpisodeNotificationContext';
import { useSessionTracking } from './hooks/useAnalytics';
import { useReferralCapture } from './hooks/useReferralCapture';
import { Toaster } from 'react-hot-toast';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingModal from './components/onboarding/OnboardingModal';
import Tutorial from './components/Tutorial';
import SubscriptionModal from './components/SubscriptionModal';
import LandingPage from './pages/LandingPage';
import Characters from './pages/Characters';
import CharacterLab from './pages/CharacterLab';
import Storyverse from './pages/Storyverse';
import Create from './pages/Create';
import Library from './pages/Library';
import AudiobookLibrary from './pages/AudiobookLibrary';
import AudiobookCreatePage from './pages/AudiobookCreatePage';
import AIVoiceStorybookCreatePage from './pages/AIVoiceStorybookCreatePage';
import AudiobookPlayer from './pages/AudiobookPlayer';
import CreateCharacter from './pages/CreateCharacter';
import CreateStory from './pages/CreateStory';
import StoryTemplatePage from './pages/StoryTemplatePage';
import TemplateDetailPage from './pages/TemplateDetailPage';
import CharacterDetail from './pages/CharacterDetail';
import StoryDetail from './pages/StoryDetail';
import ShowDetail from './pages/ShowDetail';
import StandalonePlayer from './pages/StandalonePlayer';
// import Sitemap from './components/Sitemap';
import AuthPage from './pages/AuthPage';
import FunnelAuthPage from './pages/FunnelAuthPage';
import InvitePage from './pages/InvitePage';
import SubscriptionSettings from './pages/SubscriptionSettings';
import Settings from './pages/Settings';
import ProfilesPage from './pages/ProfilesPage';
import LikedShows from './pages/Profile/LikedShows';
import GiftPage from './pages/GiftPage';
import RedeemPage from './pages/RedeemPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import ShareCharacter from './pages/ShareCharacter';
import ShareEpisode from './pages/ShareEpisode';
import TestBackgroundTasks from './pages/TestBackgroundTasks';
import ExampleVideo from './pages/ExampleVideo';
import DownloadPage from './pages/DownloadPage';
import WeeklyChallengePage from './pages/WeeklyChallengePage';
import GamesPage from './pages/GamesPage';
import BubblePopPage from './pages/BubblePopPage';
import ColorBubblePopPage from './pages/ColorBubblePopPage';
import StoryPuzzlePage from './pages/StoryPuzzlePage';
import AnimalSoundFarmPage from './pages/AnimalSoundFarmPage';
import WordWebDefenderPage from './pages/WordWebDefenderPage';
import MathCastleDefensePage from './pages/MathCastleDefensePage';
import FeedMonsterPage from './pages/FeedMonsterPage';
import CritterCrossingPage from './pages/CritterCrossingPage';
import CityHelpersPage from './pages/CityHelpersPage';
import DesignSystem from './pages/DesignSystem';
import TestLoaders from './pages/TestLoaders';
import Toast from './components/Toast';
import CloudReveal from './components/games/CloudTransition';
import LoadingScreen from './components/LoadingScreen';
import BackgroundTaskNotifications from './components/BackgroundTaskNotifications';
import VideoDownloadNotification from './components/VideoDownloadNotification';
import './index.css';

// Redirect non-www → www before React mounts so OAuth PKCE sessionStorage stays
// on the same origin. pixleyai.com and www.pixleyai.com are different origins,
// so sessionStorage written on one is invisible to the other — causing the first
// Google login attempt to fail because the PKCE code verifier is missing.
if (window.location.hostname === 'pixleyai.com') {
  window.location.replace(window.location.href.replace('://pixleyai.com', '://www.pixleyai.com'));
}

// Layout component that handles navigation visibility
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { loading } = useAuth();
  const { showOnboarding } = useOnboarding();

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  // While onboarding is active, render ONLY the onboarding modal — no navbar and
  // no routes behind it. Previously the routes (e.g. Storyverse) + navbar still
  // rendered under the overlay and bled through, since nothing blocked them.
  if (showOnboarding) {
    return <OnboardingModal />;
  }

  // Don't show navigation on auth page or public pages. Share links DO show the
  // navbar (so viewers can navigate into Pixley / sign up), matching the legacy
  // /shareCharacters and /shareEpisodes routes.
  // Blog (/blog, /blog/:slug) intentionally shows Navigation — same shared
  // toolbar as Landing and Storyverse. BlogList/BlogPost already pad content
  // clear of the fixed navbar (isBlogPage / mergedLight styling in Navigation).
  const hideNavigation = ['/auth', '/funnel', '/funnel-auth', '/invite', '/privacy-policy', '/example', '/download', '/design-system'].includes(location.pathname) ||
                         location.pathname === '/games/math-castle-defense' ||
                         location.pathname === '/__preview/math-castle-defense' ||
                         location.pathname.startsWith('/sitemap');

  return (
    <>
      {!hideNavigation && <Navigation />}
      <main>
        {children}
      </main>
    </>
  );
};

// Redirect helper that preserves query params (UTMs, gclid, fbclid, etc.)
const RedirectWithParams = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

// Root route - landing page for guests, Storyverse for signed-in users
const RootRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  // Signed-in users go to Storyverse — preserve UTMs/gclid/fbclid from ad clicks
  if (user) {
    return <Navigate to={`/storyverse${location.search}`} replace />;
  }

  return <LandingPage />;
};

// Session tracking wrapper
const AppContent = () => {
  const { user, loading } = useAuth();
  const { apiClient } = useAPI();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useSessionTracking();
  useReferralCapture(); // CRITICAL: Capture referral ID from URL on app load

  // overflow-x uses clip (not hidden): clip doesn't create a scroll
  // container, so position:sticky keeps working for pinned sections
  return (
    <div
      className="App min-h-screen bg-gradient-to-br from-gray-900 to-gray-900 text-gray-900"
      style={{ overflowX: "clip" }}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          style: { zIndex: 100000 },
        }}
        containerStyle={{
          zIndex: 100000,
          top: 96,
        }}
      />
      <Toast />
      <CloudReveal />
      <AppLayout>
                  <Routes>
                  {/* Root route */}
                  <Route path="/" element={<RootRedirect />} />
                  
                  {/* Auth route */}
                  <Route path="/auth" element={<AuthPage />} />
                  {/* Referral landing: public, captures ?ref= then routes into the funnel. */}
                  <Route path="/invite" element={<InvitePage />} />
                  <Route path="/funnel" element={<RedirectWithParams to="/funnel-auth" />} />
                  <Route path="/funnel-auth" element={<FunnelAuthPage />} />
                  
                  {/* Public Storyverse - accessible to everyone */}
                  <Route path="/storyverse" element={<Storyverse />} />

                  <Route path="/create" element={
                    <ProtectedRoute>
                      <Create />
                    </ProtectedRoute>
                  } />

                  <Route path="/library" element={
                    <ProtectedRoute>
                      <Library />
                    </ProtectedRoute>
                  } />

                  <Route path="/weekly-challenge" element={
                    <ProtectedRoute>
                      <WeeklyChallengePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games" element={
                    <ProtectedRoute>
                      <GamesPage />
                    </ProtectedRoute>
                  } />

                  {/* Deliberately PUBLIC: beat-my-score challenge links land
                      here, and the recipient usually has no account yet.
                      Guests can play; saving progress and the games hub stay
                      behind auth (the page itself guards those). */}
                  <Route path="/games/bubble-pop" element={<BubblePopPage />} />

                  <Route path="/games/color-bubble-pop" element={
                    <ProtectedRoute>
                      <ColorBubblePopPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/story-puzzle" element={
                    <ProtectedRoute>
                      <StoryPuzzlePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/animal-sound-farm" element={
                    <ProtectedRoute>
                      <AnimalSoundFarmPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/word-web-defender" element={
                    <ProtectedRoute>
                      <WordWebDefenderPage />
                    </ProtectedRoute>
                  } />



                  <Route path="/games/math-castle-defense" element={
                    <ProtectedRoute>
                      <MathCastleDefensePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/feed-monster" element={
                    <ProtectedRoute>
                      <FeedMonsterPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/critter-crossing" element={
                    <ProtectedRoute>
                      <CritterCrossingPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/games/city-helpers" element={
                    <ProtectedRoute>
                      <CityHelpersPage />
                    </ProtectedRoute>
                  } />

                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/math-castle-defense" element={<MathCastleDefensePage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/feed-monster" element={<FeedMonsterPage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/critter-crossing" element={<CritterCrossingPage />} />
                  )}
                  {process.env.NODE_ENV === 'development' && (
                    <Route path="/__preview/city-helpers" element={<CityHelpersPage />} />
                  )}


                  <Route path="/audiobooks" element={
                    <ProtectedRoute>
                      <AudiobookLibrary />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/create" element={
                    <ProtectedRoute>
                      <AudiobookCreatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/create-ai-voice" element={
                    <ProtectedRoute>
                      <AIVoiceStorybookCreatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/audiobooks/:audiobookId" element={<AudiobookPlayer />} />

                  {/* Storybooks (public-facing URL; same player as audiobooks) */}
                  <Route path="/storybooks/:audiobookId" element={<AudiobookPlayer />} />

                  {/* Legacy routes - keep for backwards compatibility */}
                  <Route path="/character-lab" element={
                    <ProtectedRoute>
                      <CharacterLab />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/characters" element={
                    <ProtectedRoute>
                      <Characters />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/characters/:id" element={
                    <ProtectedRoute>
                      <CharacterDetail />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/create-character" element={
                    <ProtectedRoute>
                      <CreateCharacter />
                    </ProtectedRoute>
                  } />
                  
                  {/* Public story detail - guests can view public stories */}
                  <Route path="/stories/:id" element={<StoryDetail />} />
                  
                  <Route path="/create-story" element={
                    <ProtectedRoute>
                      <CreateStory />
                    </ProtectedRoute>
                  } />

                  <Route path="/story-template" element={
                    <ProtectedRoute>
                      <StoryTemplatePage />
                    </ProtectedRoute>
                  } />

                  <Route path="/story-template/:templateId" element={
                    <ProtectedRoute>
                      <TemplateDetailPage />
                    </ProtectedRoute>
                  } />

                  {/* Public Storyverse show detail - guests can view but can't watch episodes */}
                  <Route path="/storyverse/shows/:id" element={<ShowDetail isStoryverse={true} />} />

                  {/* Standalone show — plays first episode directly */}
                  <Route path="/storyverse/standalone/:id" element={<StandalonePlayer />} />

                  {/* <Route path="/subscription-settings" element={
                    <ProtectedRoute>
                      <SubscriptionSettings />
                    </ProtectedRoute>
                  } /> */}
                  
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />

                  <Route path="/profiles" element={
                    <ProtectedRoute>
                      <ProfilesPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/profile/liked" element={
                    <ProtectedRoute>
                      <LikedShows />
                    </ProtectedRoute>
                  } />

                  <Route path="/gift" element={
                    <ProtectedRoute>
                      <GiftPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/redeem" element={<RedeemPage />} />

                  <Route path="/test-background-tasks" element={
                    <ProtectedRoute>
                      <TestBackgroundTasks />
                    </ProtectedRoute>
                  } />
                  
                  {/* Design System reference page */}
                  <Route path="/design-system" element={<DesignSystem />} />

                  {/* Temp: loader test page */}
                  <Route path="/test-loaders" element={<TestLoaders />} />

                  {/* Public routes */}
                  {/* Landing page moved to the root; keep old links working */}
                  <Route path="/about" element={<RedirectWithParams to="/" />} />
                  <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/example" element={<ExampleVideo />} />
                  <Route path="/download" element={<DownloadPage />} />

                  {/* Blog routes */}
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  
                  {/* Sitemap routes - dynamically fetch from API */}
                  {/* <Route path="/sitemap" element={<Sitemap />} />
                  <Route path="/sitemap.xml" element={<Sitemap />} />
                  <Route path="/sitemap/:sitemapFile" element={<Sitemap />} /> */}

                  {/* Old share routes (legacy) */}
                  <Route path="/shareCharacters/:id" element={<ShareCharacter />} />
                  <Route path="/shareEpisodes/:id" element={<ShareEpisode />} />

                  {/* New public share routes */}
                  <Route path="/share/character/:id" element={<CharacterDetail isPublic={true} />} />
                  <Route path="/share/episode/:id" element={<StoryDetail isPublic={true} />} />

                  {/* Catch all route - redirect to home */}
                  <Route path="*" element={<RedirectWithParams to="/" />} />
                  </Routes>
                </AppLayout>

                {/* Global components (OnboardingModal is rendered by AppLayout
                    as a blocking gate when onboarding is active) */}
                <SubscriptionModal
                  isOpen={showSubscriptionModal}
                  onClose={() => setShowSubscriptionModal(false)}
                  apiClient={apiClient}
                  user={user}
                />
                <Tutorial />
                <BackgroundTaskNotifications />
                <VideoDownloadNotification />
              </div>
  );
};

function App() {
  return (
    <PostHogProvider>
      <AuthProvider>
        <APIProvider>
          <ProfileProvider>
            <OnboardingProvider>
              <TutorialProvider>
                <SearchProvider>
                  <EpisodeNotificationProvider>
                    <Router>
                      <RouteSeo />
                      <AppContent />
                    </Router>
                  </EpisodeNotificationProvider>
                </SearchProvider>
              </TutorialProvider>
            </OnboardingProvider>
          </ProfileProvider>
        </APIProvider>
      </AuthProvider>
    </PostHogProvider>
  );
}

export default App;
